// frontend/src/pages/PostScheduling.js
import '../custom.css'

import React from 'react'
import {
  Container,
  Row,
  Col,
  Image,
  Button,
  Card,
  Accordion,
} from 'react-bootstrap'
import { CheckCircleFill } from 'react-bootstrap-icons'

/* placeholder screenshots (swap for real ones later) */
const calendarMock =
  '/assets/logo/calender.webp'
const optimalTimeMock =
  'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80'

export default function PostScheduling () {
  return (
    <>
      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft text-center'>
        <div className='mx-auto px-4 px-lg-5' style={{ maxWidth: 960 }}>
          <h1 className='display-5 fw-bold mb-3'>Post Scheduling</h1>
          <p className='lead mb-4'>
            Queue, reschedule, and auto-publish content to every platform at the
            perfect time — all from one drag-and-drop calendar.
          </p>
          <Button variant='primary' size='lg' href='/register'>
            Try it Free
          </Button>
        </div>
      </section>

      {/* ───────── CALENDAR SHOWCASE ───────── */}
      <section className='py-5'>
        <Container fluid='lg'>
          <Row className='align-items-center g-5'>
            <Col md={6}>
              <Image
                src={calendarMock}
                alt='Visual content calendar'
                fluid
                className='shadow-card rounded-4'
              />
            </Col>
            <Col md={6}>
              <h2 className='fw-bold mb-3'>Your social calendar at a glance</h2>
              <p className='text-muted fs-5'>
                Drag posts to new dates, filter by platform or campaign, and
                spot content gaps in seconds.
              </p>
              {[
                'Week, month & list views',
                'Color-coded by platform',
                'Quick-edit captions inline',
                'Bulk move or duplicate',
              ].map(txt => (
                <p key={txt} className='d-flex align-items-start gap-2 mb-2'>
                  <CheckCircleFill className='text-success mt-1' /> {txt}
                </p>
              ))}
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── OPTIMAL TIME ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg'>
          <Row className='align-items-center g-5 flex-md-row-reverse'>
            <Col md={6}>
              <Image
                src={optimalTimeMock}
                alt='Optimal post-time suggestions'
                fluid
                className='shadow-card rounded-4'
              />
            </Col>
            <Col md={6}>
              <h2 className='fw-bold mb-3'>Never guess the best time again</h2>
              <p className='text-muted fs-5'>
                Greatly Social analyses your historical engagement and global
                audience to surface <strong>Best Time to Post</strong>{' '}
                suggestions for every network, every day.
              </p>
              <p className='text-muted mb-0'>
                Just toggle <em>“Auto-schedule”</em> and your post lands when
                your followers are most active.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── BENEFIT CARDS ───────── */}
      <section className='py-5'>
        <Container fluid='lg'>
          <Row className='justify-content-center mb-5'>
            <Col lg={7} className='text-center'>
              <h2 className='fw-bold mb-4'>
                Built to save you time — and headaches
              </h2>
            </Col>
          </Row>

          <Row className='g-4'>
            {[
              {
                title: 'Bulk Uploader',
                body: 'Import up to 100 posts via CSV or Google Sheet and schedule in one click.',
              },
              {
                title: 'Recurring Slots',
                body: 'Lock in “Motivation Monday” or “Friday Fun” spots that auto-refill each week.',
              },
              {
                title: 'Approval Workflow',
                body: 'Invite clients or teammates to approve drafts right on the calendar.',
              },
            ].map(({ title, body }, i) => (
              <Col md={4} key={i}>
                <Card className='h-100 shadow-card'>
                  <Card.Body>
                    <Card.Title className='fw-semibold'>{title}</Card.Title>
                    <Card.Text className='text-muted'>{body}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-4 text-center'>Scheduling FAQs</h2>
          <Accordion flush defaultActiveKey='0'>
            {[
              [
                'Can I post to multiple platforms at once?',
                'Yes. Create one post, tailor captions per platform, and Greatly Social publishes each version at the right time.',
              ],
              [
                'Do you support carousel images and reels?',
                'Absolutely — upload up to 10 photos for carousels or snag our vertical-first video templates for reels & shorts.',
              ],
              [
                'What happens if I exceed my daily quota?',
                'Your extra posts stay in the queue. Upgrade anytime or wait for the next cycle and they’ll publish automatically.',
              ],
            ].map(([q, a], idx) => (
              <Accordion.Item eventKey={String(idx)} key={idx}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto' style={{ maxWidth: 720 }}>
          <h3 className='fw-bold mb-3'>Ready to master your social schedule?</h3>
          <p className='lead mb-4'>
            Start your free 14-day trial of Greatly Social and watch your
            engagement climb.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-3'>
            Start Free Trial
          </Button>
          <Button variant='outline-light' size='lg' href='/plans'>
            View Plans
          </Button>
        </div>
      </section>
    </>
  )
}
